import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PermissionKey,
  ALL_PERMISSIONS,
} from '@mesterplan/shared-types';
import type { PermissionTeamDto } from '@mesterplan/api-client';
import { useFirebase } from '../context';
import { CACHE_KEYS, NotificationCallbacks, CompanyContext } from '../types';

/**
 * Options for permission operations hook.
 */
export interface UsePermissionOperationsOptions {
  notifications?: NotificationCallbacks;
  selectedCompany?: CompanyContext | null;
}

/**
 * Data for updating a single permission.
 *
 * No `companyId`: every one of these endpoints resolves the org from the
 * authenticated session (the active-org header), so a company id in the payload
 * was never sent and could never scope the write. Carrying one invited a caller
 * to pass company A, watch the write land on the session's company B, and
 * believe it had targeted A. Scope comes from `selectedCompany` / the session,
 * and from nowhere else.
 */
export interface UpdateSinglePermissionData {
  /** `'admin'`, or a team's `teams.id` guid. */
  role: string;
  permission: PermissionKey;
  value: boolean;
}

/**
 * Data for resetting permissions to defaults. See {@link UpdateSinglePermissionData}
 * for why there is no `companyId`.
 */
export interface ResetPermissionsData {
  /** `'admin'`, or a team's `teams.id` guid. */
  role: string;
}

/**
 * Hook providing role/team permission configuration, backed entirely by the
 * .NET permissions controller.
 *
 * Only owners (and admins with settings.managePermissions) can modify
 * permissions; that is enforced server-side.
 *
 * @example
 * ```tsx
 * const { useUpdateSinglePermission } = usePermissionOperations({
 *   notifications: { onSuccess: toast.success, onError: toast.error },
 *   selectedCompany: { id: company.id },
 * });
 *
 * useUpdateSinglePermission().mutate({
 *   role: team.id,
 *   permission: 'planning.view',
 *   value: true,
 * });
 * ```
 */
export function usePermissionOperations(options: UsePermissionOperationsOptions = {}) {
  const queryClient = useQueryClient();
  const { api } = useFirebase();
  const { notifications, selectedCompany } = options;

  /**
   * Permissions live only in the .NET aggregate now. The old Firestore branch
   * wrote `COMPANIES.settings.permissions.*` and was gated on the Firebase
   * `currentUser`, which is null on the IDP path (`App.tsx` mounts
   * `FirebaseProvider currentUser={firebaseUser}` and `IdpGatedApp` passes
   * `firebaseUser={null}`) — so that branch could only ever throw before it was
   * reached. A missing client here is a wiring bug, not a legacy company, and
   * should say so rather than degrade to a write that cannot succeed.
   */
  const requireApi = () => {
    if (!api) {
      throw new Error('Tilladelser kræver .NET-klienten (ingen MesterplanApi monteret)');
    }
    return api;
  };

  /**
   * Send a SINGLE key's grant/revoke to .NET. `role === 'admin'` targets the admin
   * role; any other value is a team's `teams.id` guid.
   *
   * This deliberately sends only the delta. The full-set-replace endpoints
   * (`setAdmin`/`setTeam`) require reading the current set first and PUTting the
   * whole thing back, so two admins toggling different permissions concurrently
   * both seed from the same baseline and the second write silently drops the
   * first — a lost update with no error on either side. The server now applies
   * the delta to the effective set inside one request, so the client never holds
   * the rest of the set and cannot overwrite it.
   */
  const toggleNetPermission = async (
    role: string,
    permission: PermissionKey,
    value: boolean,
  ): Promise<void> => {
    const client = requireApi();
    const res = role === 'admin'
      ? await client.permissions.toggleAdmin({ permission, value })
      : await client.permissions.toggleTeam(role, { permission, value });
    if (!res.ok) {
      throw new Error(res.error?.detail ?? res.error?.title ?? 'Kunne ikke opdatere tilladelser');
    }
  };

  /** Invalidate the .NET config query so a write's effect is re-read for display. */
  const invalidatePermissionConfig = (companyId: string | undefined) => {
    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PERMISSION_CONFIG, companyId] });
  };

  /**
   * Read the company's EFFECTIVE role/team permission config from .NET, for both
   * a truthful settings display and the write RMW. Returns a `resolveRole` helper
   * that maps a role/team to a flat `{ key: boolean }` map (effective granted →
   * true, everything else → false). The server fills in defaults, so this is
   * exactly what the server enforces — no client-side default reconstruction.
   */
  const useFetchPermissionConfig = (companyId?: string) => {
    const targetCompanyId = companyId || selectedCompany?.id;

    return useQuery({
      queryKey: [CACHE_KEYS.PERMISSION_CONFIG, targetCompanyId],
      enabled: !!api && !!targetCompanyId,
      queryFn: async (): Promise<{
        resolveRole: (role: string) => Record<PermissionKey, boolean>;
        /**
         * The org's teams, in the admin's `sortOrder`, archived ones included — the exact tab
         * strip the permissions page renders. Built server-side from the real team rows, which
         * is why it is the only correct source: `config.teams` is guid-keyed only (one entry
         * per team), but `Object.keys` would still give an unstable, un-sorted tab order —
         * `sortOrder` lives on `teamList`, not on the map.
         */
        teamList: PermissionTeamDto[];
      } | null> => {
        const res = await requireApi().permissions.getConfig();
        if (!res.ok) {
          throw new Error(res.error?.detail ?? res.error?.title ?? 'Kunne ikke hente tilladelser');
        }

        const { admin, teams, teamList } = res.data;
        return {
          resolveRole: (role: string): Record<PermissionKey, boolean> => {
            const granted = role === 'admin' ? admin : (teams[role] ?? []);
            const map = {} as Record<PermissionKey, boolean>;
            for (const p of ALL_PERMISSIONS) {
              map[p.key] = false;
            }
            for (const key of granted) {
              map[key as PermissionKey] = true;
            }
            return map;
          },
          teamList,
        };
      },
    });
  };

  /**
   * Update a single permission for admin or a team.
   *
   * One request carrying just the delta — the server merges it into the role's
   * current effective set. Previously this read the whole config and PUT the
   * whole set back, which lost concurrent edits to other keys.
   */
  const useUpdateSinglePermission = () => {
    return useMutation({
      mutationFn: async (data: UpdateSinglePermissionData) => {
        await toggleNetPermission(data.role, data.permission, data.value);
        return { success: true };
      },
      onSuccess: () => {
        if (selectedCompany?.id) {
          queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.COMPANY, selectedCompany.id] });
        }
        // Re-read the .NET config so the toggle's persisted effect drives the
        // display (the SPA's settings blob never reflects this store).
        invalidatePermissionConfig(selectedCompany?.id);
        notifications?.onSuccess?.('Tilladelse opdateret');
      },
      onError: (error) => {
        console.error('Error updating permission:', error);
        notifications?.onError?.('Kunne ikke opdatere tilladelse');
      },
    });
  };

  /**
   * Reset a role's permissions to the server defaults.
   *
   * Resolved SERVER-SIDE via the dedicated reset endpoints, so the default that
   * takes effect is the C# `PermissionDefaults` baseline the resolver enforces —
   * never a client default that could diverge (the TS admin default does diverge
   * from C#).
   */
  const useResetPermissions = () => {
    return useMutation({
      mutationFn: async (data: ResetPermissionsData) => {
        const client = requireApi();
        const res = data.role === 'admin'
          ? await client.permissions.resetAdmin()
          : await client.permissions.resetTeam(data.role);
        if (!res.ok) {
          throw new Error(res.error?.detail ?? res.error?.title ?? 'Kunne ikke nulstille tilladelser');
        }
        return { success: true };
      },
      onSuccess: () => {
        if (selectedCompany?.id) {
          queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.COMPANY, selectedCompany.id] });
        }
        invalidatePermissionConfig(selectedCompany?.id);
        notifications?.onSuccess?.('Tilladelser nulstillet til standard');
      },
      onError: (error) => {
        console.error('Error resetting permissions:', error);
        notifications?.onError?.('Kunne ikke nulstille tilladelser');
      },
    });
  };

  return {
    useUpdateSinglePermission,
    useResetPermissions,
    useFetchPermissionConfig,
  };
}
